import { axiosInstance } from './axios';

export const UNLIMITED = -1;

export interface Plan {
  code: string;
  name: string;
  tagline: string;
  amount: number;        // minor units (cents)
  amount_major: number;
  currency: string;
  days: number;
  max_tracked_applications: number;
  max_cv_analyses: number;
  interview_roadmap: boolean;
  job_alerts: boolean;
  highlight: boolean;
}

export interface Entitlements {
  plan: Plan;
  expires_at: string | null;
  is_paid: boolean;
  usage: { tracked_applications: number; cv_analyses: number };
  remaining: { tracked_applications: number; cv_analyses: number };
}

export interface PaywallDetail {
  error: 'upgrade_required';
  message: string;
  feature: string;
  limit: number;
}

/** Narrow an axios error into a paywall prompt, or null if it is something else. */
export const asPaywall = (error: any): PaywallDetail | null => {
  const detail = error?.response?.status === 402 ? error.response.data?.detail : null;
  return detail?.error === 'upgrade_required' ? (detail as PaywallDetail) : null;
};

export const formatPrice = (plan: Plan) =>
  new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: plan.currency || 'ZAR',
    minimumFractionDigits: 0,
  }).format(plan.amount_major);

export const billingApi = {
  getPlans: async (): Promise<{ plans: Plan[]; currency: string; payments_enabled: boolean }> => {
    const response = await axiosInstance.get('/billing/plans');
    return response.data;
  },

  getEntitlements: async (): Promise<Entitlements> => {
    const response = await axiosInstance.get('/billing/me');
    return response.data;
  },

  /** Returns the Paystack hosted checkout URL to redirect the browser to. */
  startCheckout: async (planCode: string): Promise<{ authorization_url: string }> => {
    const response = await axiosInstance.post('/billing/checkout', { plan_code: planCode });
    return response.data;
  },

  verify: async (reference: string) => {
    const response = await axiosInstance.get('/billing/verify', { params: { reference } });
    return response.data;
  },
};
